import React, { useEffect, useState, useRef } from "react";
import { ChartLine } from "@phosphor-icons/react";
import { api } from "../../lib/api";

/**
 * Bandeau rouge « 🔴 Chiffres live à citer » — affiché en tête du portail
 * télépro (mode simple ET mode liste) pour que le télépro puisse citer
 * en direct les chiffres du site pendant CHAQUE appel.
 *
 * Comportement :
 * - Fetch initial /telepro/me/live-stats
 * - Repoll toutes les 15 s (backend cache = 20 s)
 * - Entre 2 fetches, tick optimiste toutes les ~7 s calibré sur
 *   avg_searches_per_day_7d → le compteur bouge en continu
 * - Flash rouge de 700 ms à chaque incrément pour attirer l'œil
 */
// Cache module-level partagé entre remounts (mode simple ↔ liste)
let _cache = null;
let _cacheExp = 0;
const _TTL = 15 * 1000;

async function fetchStats() {
  const now = Date.now();
  if (_cache && now < _cacheExp) return _cache;
  try {
    const r = await api.get("/telepro/me/live-stats");
    _cache = r.data;
    _cacheExp = now + _TTL;
    return r.data;
  } catch { return null; }
}

function fmt(n) {
  if (n == null) return "—";
  try { return Number(n).toLocaleString("fr-FR").replace(/\u202F/g, " "); }
  catch { return String(n); }
}

export default function LiveStatsBanner({ compact = false }) {
  const [stats, setStats] = useState(_cache);
  const [displayedTotal, setDisplayedTotal] = useState(_cache?.total_searches || 0);
  const [flash, setFlash] = useState(false);
  const prevRef = useRef(_cache?.total_searches || 0);

  // Polling backend
  useEffect(() => {
    let alive = true;
    const load = async () => {
      const s = await fetchStats();
      if (!alive || !s) return;
      setStats(s);
      const t = s.total_searches || 0;
      if (t > prevRef.current) {
        setDisplayedTotal(t);
        setFlash(true);
        setTimeout(() => setFlash(false), 700);
        prevRef.current = t;
      } else if (!prevRef.current) {
        setDisplayedTotal(t);
        prevRef.current = t;
      }
    };
    load();
    const iv = setInterval(load, 15000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  // Tick optimiste entre 2 fetches
  useEffect(() => {
    const per = stats?.avg_searches_per_day_7d;
    if (!per || per <= 0) return;
    const perSec = per / 86400;
    const iv = setInterval(() => {
      const bump = Math.max(1, Math.round(perSec * 7));
      setDisplayedTotal((v) => v + bump);
      setFlash(true);
      setTimeout(() => setFlash(false), 700);
    }, 6500 + Math.random() * 2000);
    return () => clearInterval(iv);
  }, [stats?.avg_searches_per_day_7d]);

  if (!stats) return null;
  if (!stats.avg_searches_per_day_7d && stats.total_searches < 100) return null;

  const perDay = stats.avg_searches_per_day_7d || 0;
  const perMonth = perDay * 30;

  return (
    <div
      className={`rounded-sm border-l-4 border-[#B33A3A] bg-[#B33A3A]/8 ${compact ? "p-2" : "p-3"}`}
      data-testid="telepro-live-stats-banner"
    >
      <div className="flex items-center gap-1.5 text-[#B33A3A] font-bold text-[10px] uppercase tracking-widest mb-1.5">
        <ChartLine size={12} weight="fill"/> 🔴 Chiffres live à citer pendant l'appel
      </div>
      <div className="grid grid-cols-2 gap-2 mb-1.5">
        <div className="bg-white/70 rounded-sm p-1.5">
          <div className="font-display text-base font-semibold tabular-nums leading-none" data-testid="live-banner-per-day">
            {fmt(perDay)}
          </div>
          <div className="text-[9px] uppercase tracking-wider text-muted-foreground mt-0.5">recherches / jour (moy 7j)</div>
        </div>
        <div className="bg-white/70 rounded-sm p-1.5">
          <div
            className={`font-display text-base font-semibold tabular-nums leading-none transition-colors duration-300 ${flash ? "text-[#B33A3A]" : ""}`}
            data-testid="live-banner-total"
          >
            {fmt(displayedTotal)}
          </div>
          <div className="text-[9px] uppercase tracking-wider text-muted-foreground mt-0.5">recherches depuis le lancement</div>
        </div>
      </div>
      <div className="text-[11px] text-foreground/85 leading-snug italic" data-testid="live-banner-script">
        💬 « Notre site enregistre en moyenne <b>{fmt(perDay)} recherches / jour</b>{" "}
        — soit ~<b>{fmt(perMonth)} personnes / mois</b> qui cherchent une entreprise
        dans le 68. Autant en profiter pour être en haut de la liste. »
      </div>
    </div>
  );
}
